import { create } from 'zustand';
import { api } from './api';

export type BarristerRank = 'silk' | 'junior' | 'pupil' | 'door_tenant';
export type BarristerAvailability =
  | 'available'
  | 'partial'
  | 'busy'
  | 'on_leave'
  | 'unavailable';

export interface ApiBarrister {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    memberType: BarristerRank;
    status: string;
    avatarUrl: string | null;
    jobTitle: string | null;
    lastLoginAt: string | null;
  };
  fullName: string;
  shortName: string;
  initials: string;
  callYear: number | null;
  silkYear: number | null;
  inn: string | null;
  seniority: number;
  availability: BarristerAvailability;
  practiceAreas: Array<{
    id: string;
    name: string;
    slug: string;
    expertiseLevel: string;
    yearsExp: number | null;
  }>;
  publicProfile: boolean;
  publicSlug: string | null;
}

export async function fetchBarristers(): Promise<ApiBarrister[]> {
  const res = await api.get('/barristers', { params: { limit: 200 } });
  const payload = res.data?.data ?? [];
  return Array.isArray(payload) ? payload : [];
}

export interface BarristerRateCard {
  hourlyRate: number | null;
  briefFeeMin: number | null;
  refresherRate: number | null;
  vatRegistered: boolean;
  vatNumber: string | null;
}

export async function fetchBarristerRateCard(id: string): Promise<BarristerRateCard | null> {
  const res = await api.get(`/barristers/${id}/rate`);
  return res.data?.data ?? null;
}

export async function fetchBarrister(id: string): Promise<ApiBarrister> {
  const res = await api.get(`/barristers/${id}`);
  return res.data?.data;
}

// ─── Zustand store: shared cache for the barristers list ─────
// Replaces the mock seed-based barristersStore.ts for any new
// component that wants real chambers staff. The mock store
// remains for legacy pages that still use it — wire those over
// in batches, not as part of an unrelated feature.

interface BarristersState {
  barristers: ApiBarrister[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
  fetch: () => Promise<void>;
  refetch: () => Promise<void>;
}

// ─── Availability API ─────────────────────────────────────────

export interface PendingAvailability {
  id: string;
  barristerId: string;
  barristerName: string;
  date: string;
  status: BarristerAvailability;
  notes: string | null;
  createdAt: string;
}

export interface AvailabilitySlot {
  id: string;
  barristerId: string;
  date: string;
  status: BarristerAvailability;
  notes: string | null;
  approved: boolean;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionNote: string | null;
  createdAt: string;
}

export async function fetchPendingAvailability(): Promise<PendingAvailability[]> {
  const res = await api.get('/barristers/availability/pending');
  return res.data?.data ?? [];
}

export async function approveAvailability(availabilityId: string): Promise<void> {
  await api.post(`/barristers/availability/${availabilityId}/approve`);
}

export async function rejectAvailability(
  availabilityId: string,
  rejectionNote?: string,
): Promise<void> {
  await api.post(`/barristers/availability/${availabilityId}/reject`, { rejectionNote });
}

export async function setBarristerAvailability(
  barristerId: string,
  date: string,
  status: BarristerAvailability,
  notes?: string,
): Promise<AvailabilitySlot> {
  const res = await api.post(`/barristers/${barristerId}/availability`, {
    date,
    status,
    notes,
  });
  return res.data?.data;
}

export async function getBarristerAvailability(
  barristerId: string,
  startDate: string,
  endDate: string,
): Promise<AvailabilitySlot[]> {
  const res = await api.get(`/barristers/${barristerId}/availability`, {
    params: { startDate, endDate },
  });
  return res.data?.data ?? [];
}

export const useApiBarristersStore = create<BarristersState>()((set, get) => ({
  barristers: [],
  loading: false,
  error: null,
  loaded: false,

  fetch: async () => {
    if (get().loaded || get().loading) return;
    set({ loading: true, error: null });
    try {
      const barristers = await fetchBarristers();
      set({ barristers, loading: false, loaded: true });
    } catch (err) {
      const msg = (err as Error)?.message ?? 'Failed to load barristers';
      set({ loading: false, error: msg });
    }
  },

  refetch: async () => {
    set({ loading: true, error: null });
    try {
      const barristers = await fetchBarristers();
      set({ barristers, loading: false, loaded: true });
    } catch (err) {
      const msg = (err as Error)?.message ?? 'Failed to load barristers';
      set({ loading: false, error: msg });
    }
  },
}));
